import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Package, Trash2, Search, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function FinanceCustomItemCard({ product, index, onChange, onRemove, canRemove, botolList = [], tutupList = [], sprayList = [], bahanList = [], itemTambahanList = [], fullsetList = [] }) {
  const [modeInput, setModeInput] = useState('custom'); // custom | inventori
  const [activeKategori, setActiveKategori] = useState('packaging');
  const [activeSubkategori, setActiveSubkategori] = useState('Semua');
  const [inventorySearch, setInventorySearch] = useState('');

  const inventoryItems = useMemo(() => {
    let list = [];
    if (activeKategori === 'packaging') {
      const b = botolList.map(i => ({ ...i, __table: 'Botol', __sub: 'Botol' }));
      const t = tutupList.map(i => ({ ...i, __table: 'Tutup', __sub: 'Tutup' }));
      const s = sprayList.map(i => ({ ...i, __table: 'Spray', __sub: 'Spray' }));
      const f = fullsetList.map(i => ({ ...i, __table: 'Fullset', __sub: 'Fullset' }));
      list = [...b, ...t, ...s, ...f];
      if (activeSubkategori !== 'Semua') {
        list = list.filter(i => i.__sub === activeSubkategori);
      }
    } else if (activeKategori === 'bahan_cair') {
      list = bahanList.map(i => ({ ...i, __table: 'BahanCair', __sub: i.kategori || 'Lainnya' }));
      if (activeSubkategori !== 'Semua') {
        list = list.filter(i => (i.kategori || 'Lainnya') === activeSubkategori);
      }
    } else if (activeKategori === 'item_tambahan') {
      list = itemTambahanList.map(i => ({ ...i, __table: 'ItemTambahan', __sub: i.kategori || 'Lainnya' }));
      if (activeSubkategori !== 'Semua Kategori' && activeSubkategori !== 'Semua') {
        list = list.filter(i => (i.kategori || 'Lainnya') === activeSubkategori);
      }
    }
    
    if (inventorySearch) {
      const q = inventorySearch.toLowerCase();
      list = list.filter(i => (i.nama || i.nama_item || i.nama_bahan || '').toLowerCase().includes(q));
    }
    return list;
  }, [activeKategori, activeSubkategori, inventorySearch, botolList, tutupList, sprayList, fullsetList, bahanList, itemTambahanList]);

  const handleSelectInventory = (item) => {
    handleUpdateMultiple({
      nama_product: item.nama || item.nama_item || item.nama_bahan || '',
      harga_beli: item.hpp || item.harga_beli || item.modal || item.harga_rupiah || item.harga_per_satuan || '',
      harga_jual_per_pcs: item.harga_jual || item.harga_jual_satuan || '',
      inventory_item_id: item.id,
      inventory_table: item.__table
    });
  };

  const handleUpdateMultiple = (updates) => {
    const updated = { ...product, ...updates };
    const qtyStr = String(updated.qty || 1).replace(',', '.');
    const qty = parseFloat(qtyStr) || 1;
    const hrgBeli = parseFloat(updated.harga_beli) || 0;
    const hrgJual = parseFloat(updated.harga_jual_per_pcs) || 0;
    updated.total_hpp_product = hrgBeli * qty;
    updated.total_harga_jual = hrgJual * qty;
    updated.final_harga_jual_per_pcs = hrgJual;
    onChange(updated);
  };
  
  const handleUpdate = (field, value) => {
    const updated = { ...product, [field]: value };
    
    // Auto calculate totals
    const qtyStr = String(updated.qty || 1).replace(',', '.');
    const qty = parseFloat(qtyStr) || 1;
    const hrgBeli = parseFloat(updated.harga_beli) || 0;
    const hrgJual = parseFloat(updated.harga_jual_per_pcs) || 0;
    
    updated.total_hpp_product = hrgBeli * qty;
    updated.total_harga_jual = hrgJual * qty;
    updated.final_harga_jual_per_pcs = hrgJual;
    
    onChange(updated);
  };

  return (
    <div className="bg-[#FFFDF9] border border-[#E6D5B8] rounded-2xl p-5 md:p-6 shadow-sm relative transition-all">
      <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center text-white font-black shadow-lg text-sm z-10">
        {index + 1}
      </div>

      <div className="flex justify-between items-start mb-6">
        <div className="pl-4">
          <h3 className="font-bold text-[#302621] text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-[#D4AF37]" />
            Item Custom (Tanpa Produksi)
          </h3>
          <p className="text-xs text-[#7A695B] mt-1">Item ini tidak memotong inventory dan tidak masuk antrean produksi.</p>
        </div>
        
        {canRemove && (
          <Button 
            variant="ghost" 
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-3 rounded-lg flex items-center gap-1.5"
          >
            <Trash2 className="w-4 h-4" /> <span className="text-xs font-bold hidden sm:inline">Hapus Item</span>
          </Button>
        )}
      </div>

      <div className="flex justify-between items-center mb-4">
        <h4 className="font-bold text-sm text-[#302621] hidden md:block">Detail Varian</h4>
        <div className="flex gap-2 w-full md:w-auto">
          <button 
            type="button" 
            onClick={() => { setModeInput('custom'); handleUpdateMultiple({ inventory_item_id: null, inventory_table: null }); }}
            className={`flex-1 md:flex-none text-xs px-3 py-1.5 rounded-lg font-bold border transition-colors ${modeInput === 'custom' ? 'bg-[#302621] text-white border-[#302621]' : 'bg-white text-slate-500 border-[#E6D5B8] hover:bg-slate-50'}`}
          >
            Input Manual
          </button>
          <button 
            type="button" 
            onClick={() => setModeInput('inventori')}
            className={`flex-1 md:flex-none text-xs px-3 py-1.5 rounded-lg font-bold border transition-colors ${modeInput === 'inventori' ? 'bg-[#302621] text-white border-[#302621]' : 'bg-white text-slate-500 border-[#E6D5B8] hover:bg-slate-50'}`}
          >
            Pilih dari Inventori
          </button>
        </div>
      </div>

      {modeInput === 'inventori' && (
        <div className="bg-[#F9F5EC] p-4 rounded-xl border border-[#E6D5B8] mb-5 space-y-4">
          <Tabs value={activeKategori} onValueChange={(val) => { setActiveKategori(val); setActiveSubkategori(val === 'item_tambahan' ? 'Semua Kategori' : 'Semua'); }}>
            <TabsList className="grid grid-cols-3 bg-white border border-[#E6D5B8] p-1 rounded-xl w-full h-10">
              <TabsTrigger value="packaging" className="rounded-lg text-xs font-bold data-[state=active]:bg-[#8C6208] data-[state=active]:text-white">Packaging</TabsTrigger>
              <TabsTrigger value="bahan_cair" className="rounded-lg text-xs font-bold data-[state=active]:bg-[#8C6208] data-[state=active]:text-white">Bahan Cair</TabsTrigger>
              <TabsTrigger value="item_tambahan" className="rounded-lg text-xs font-bold data-[state=active]:bg-[#8C6208] data-[state=active]:text-white">Item Tambahan</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap gap-2">
            {activeKategori === 'packaging' && ['Semua', 'Botol', 'Tutup', 'Spray', 'Fullset'].map(sub => (
              <button type="button" key={sub} onClick={() => setActiveSubkategori(sub)} className={`px-3 py-1 rounded-full border text-[10px] font-bold transition-all ${activeSubkategori === sub ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'bg-white text-[#7A695B] border-[#E6D5B8] hover:bg-slate-50'}`}>
                {sub}
              </button>
            ))}
            {activeKategori === 'bahan_cair' && ['Semua', 'Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'PEG', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'].map(sub => (
              <button type="button" key={sub} onClick={() => setActiveSubkategori(sub)} className={`px-3 py-1 rounded-full border text-[10px] font-bold transition-all ${activeSubkategori === sub ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'bg-white text-[#7A695B] border-[#E6D5B8] hover:bg-slate-50'}`}>
                {sub}
              </button>
            ))}
            {activeKategori === 'item_tambahan' && ['Semua Kategori', 'Stiker', 'Dus', 'Sablon', 'Hot Print', 'Gift Card', 'Coloring', 'Lainnya'].map(sub => (
              <button type="button" key={sub} onClick={() => setActiveSubkategori(sub)} className={`px-3 py-1 rounded-full border text-[10px] font-bold transition-all ${activeSubkategori === sub ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'bg-white text-[#7A695B] border-[#E6D5B8] hover:bg-slate-50'}`}>
                {sub}
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari item di inventori..."
              value={inventorySearch}
              onChange={e => setInventorySearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-[#E6D5B8] rounded-lg text-xs outline-none focus:border-[#8C6208] bg-white"
            />
          </div>

          <div className="max-h-48 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
            {inventoryItems.length === 0 ? (
              <p className="text-center text-xs text-[#7A695B] py-4 bg-white rounded-lg border border-dashed border-[#E6D5B8]">Item tidak ditemukan.</p>
            ) : (
              inventoryItems.map(item => (
                <div 
                  key={item.id} 
                  onClick={() => handleSelectInventory(item)}
                  className={`flex justify-between items-center p-3 rounded-lg border cursor-pointer transition-colors ${product.inventory_item_id === item.id ? 'bg-[#D4AF37]/10 border-[#D4AF37]' : 'bg-white border-[#E6D5B8] hover:border-[#8C6208]'}`}
                >
                  <div>
                    <p className="text-xs font-bold text-[#302621]">{item.nama || item.nama_item || item.nama_bahan}</p>
                    <p className="text-[10px] text-[#7A695B] mt-0.5">Stok: <span className="font-bold">{item.stok || item.stock || 0}</span> | Modal: Rp {Number(item.hpp || item.harga_beli || item.modal || item.harga_rupiah || item.harga_per_satuan || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  {product.inventory_item_id === item.id ? (
                    <Check className="w-4 h-4 text-[#D4AF37]" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="md:col-span-2 space-y-2">
          <Label className="text-xs font-bold text-rose-600">Nama Item <span className="text-rose-500">*</span></Label>
          <Input 
            value={product.nama_product || ''} 
            onChange={e => handleUpdate('nama_product', e.target.value)} 
            placeholder="Contoh: Botol Kaca 100ml" 
            className="h-10 border-rose-200 rounded-xl bg-white" 
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-xs font-bold text-[#7A695B]">Kuantitas (Qty)</Label>
          <Input 
            type="text" 
            value={product.qty || ''} 
            onChange={e => handleUpdate('qty', e.target.value)} 
            placeholder="1"
            className="h-10 border-[#E6D5B8] rounded-xl bg-white" 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5 pt-5 border-t border-[#E6D5B8]/50">
        <div className="space-y-2">
          <Label className="text-xs font-bold text-[#7A695B]">Harga Beli / Modal per Pcs (Opsional)</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A39171]">Rp</span>
            <Input 
              type="number" 
              value={product.harga_beli || ''} 
              onChange={e => handleUpdate('harga_beli', e.target.value)} 
              placeholder="0" 
              className="h-10 pl-9 border-[#E6D5B8] rounded-xl bg-white" 
            />
          </div>
          <p className="text-[10px] text-[#A39171]">Diisi jika ingin item ini dihitung di Laporan Margin Profit.</p>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-bold text-[#D4AF37]">Harga Jual ke Klien per Pcs</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[#D4AF37]">Rp</span>
            <Input 
              type="number" 
              value={product.harga_jual_per_pcs || ''} 
              onChange={e => handleUpdate('harga_jual_per_pcs', e.target.value)} 
              placeholder="0" 
              className="h-10 pl-9 border-[#D4AF37]/50 rounded-xl bg-amber-50/50 font-bold text-[#302621] focus-visible:ring-[#D4AF37]" 
            />
          </div>
        </div>
      </div>
    </div>
  );
}
